import { SerializedStyles } from "@emotion/react";
import { ReactNode, useCallback, useState } from "react";
import { Button } from "src/components/v1";
import { downloadFile } from "src/utilities/file/download";

interface MultiDownload {
  files: { href: string; filename: string }[];
}

interface SingleDownload {
  href: string;
  filename: string;
}

interface DownloadButtonBaseProps {
  children: ReactNode;
  onClick?: () => void;
  onDownload?: () => void;
  style?: SerializedStyles;

  href?: string;
  filename?: string;
  files?: { href: string; filename: string }[];
}

type MultiDownloadProps = MultiDownload & DownloadButtonBaseProps;
type SingleDownloadProps = SingleDownload & DownloadButtonBaseProps;

export type DownloadButtonProps = MultiDownloadProps | SingleDownloadProps;

const isMultiDownload = (props: any): props is MultiDownloadProps => !!props.files;
const isSingleDownload = (props: any): props is SingleDownloadProps => !!props.filename && !!props.href;

enum DownloadState {
  NOT_DOWNLOADED = 0,
  DOWNLOADING = 1,
  DOWNLOADED = 2,
}

const DownloadButton = ({
  children,
  onClick,
  onDownload,
  style,
  filename,
  files,
  href,
}: DownloadButtonProps) => {
  const [downloadState, setDownloadState] = useState(DownloadState.NOT_DOWNLOADED);

  const handleClick = useCallback(async () => {
    onClick?.();

    setDownloadState(DownloadState.DOWNLOADING);

    const info = { filename, files, href };
    if (isMultiDownload(info)) {
      // we are a multi-download button
      await Promise.all(info.files.map((file) => downloadFile(file.href, file.filename)));
    } else if (isSingleDownload(info)) {
      await downloadFile(info.href, info.filename);
    }

    setDownloadState(DownloadState.DOWNLOADED);
    onDownload?.();
  }, [onClick, onDownload, href, filename, files]);

  return (
    <>
      <Button
        disabled={downloadState !== DownloadState.NOT_DOWNLOADED}
        onClick={handleClick}
        narrow
        style={style}
        loading={downloadState === DownloadState.DOWNLOADING}
      >
        {downloadState === DownloadState.NOT_DOWNLOADED && children}
        {downloadState === DownloadState.DOWNLOADED && "Done!"}
      </Button>
    </>
  );
};

export default DownloadButton;
